import { DataSyncModeEnum, ILogLevel, IOptions, IUser } from '@featbit/react-client-sdk'
import { isUndefined } from 'lodash'
import { useContext, useMemo, useRef } from 'react'
import useOwnedInstallations from 'src/Libs/installations/hooks/use-owned-installations'
import { useInitUserData } from 'src/Libs/user/hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { FEATURE_FLAGS_OPTIONS } from '../consts'
import { AxiosError } from 'axios'
import { E3dcFetchError } from '@e3dc-react/shell/Types/api'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { isFlowTheme } from 'src/Libs/utils'
import { resolveFeatBitUserContext } from '../utils/resolve-feat-bit-user-context'

const useFeatBitClientOptions = (): {
  fbClientOptions: IOptions | undefined
  isFbClientOptionsReady: boolean
} => {
  const userId = useTypedSelector(state => state.auth.userId)
  const isUserIdLoading = useTypedSelector(state => state.auth.isUserIdLoading)
  const isLogoutActive = useTypedSelector(state => state.app.isLogoutActive)
  const { reAuthToken } = useContext(AuthContext)

  const { user, userLoading, userContactData } = useInitUserData()
  const { isUserAnOwner, isLoading: isOwnedInstallationsLoading, isError: isOwnedInstallationsError, error } = useOwnedInstallations()

  const isNdaError =
    isOwnedInstallationsError &&
    error?.cause instanceof AxiosError &&
    (error.cause as AxiosError<E3dcFetchError>).response?.data?.attributes?.errno === 4005
  const isNotLoggedIn = (!isUserIdLoading && (isLogoutActive || userId === undefined)) || !reAuthToken
  const hasUserData = userId && user && userContactData && !isOwnedInstallationsLoading && (!isOwnedInstallationsError || isNdaError)

  /**
   * Retain the resolved user so transient loading/refetch states do not reset the FeatBit options to
   * `undefined`. The SDK rebuilds its client on every `no-user -> has-user` transition without disposing
   * the old one, so we only produce new options when the resolved user identity actually changes.
   */
  const retainedIdentityRef = useRef<string | undefined>(undefined)
  const retainedUserRef = useRef<IUser | undefined>(undefined)

  const fbClientOptions = useMemo(() => {
    const { identity, user: resolvedUser } = resolveFeatBitUserContext({
      isNotLoggedIn,
      hasUserData: Boolean(hasUserData),
      userId,
      user,
      userContactData,
      isUserAnOwner,
      isFlowTheme,
      previousIdentity: retainedIdentityRef.current,
      previousUser: retainedUserRef.current,
    })
    retainedIdentityRef.current = identity
    retainedUserRef.current = resolvedUser

    return resolvedUser ? { user: resolvedUser, ...defaultClientOptions } : undefined
  }, [isNotLoggedIn, hasUserData, userId, user, userContactData, isUserAnOwner])

  return { fbClientOptions, isFbClientOptionsReady: !userLoading && !isOwnedInstallationsLoading && !isUndefined(fbClientOptions) }
}

export default useFeatBitClientOptions

const defaultClientOptions = {
  sdkKey: FEATURE_FLAGS_OPTIONS.sdkKey,
  eventsUri: FEATURE_FLAGS_OPTIONS.eventsUri,
  dataSyncMode: DataSyncModeEnum.POLLING,
  pollingUri: FEATURE_FLAGS_OPTIONS.eventsUri,
  pollingInterval: 60000 * 2 /** 2min */,
  logLevel: 'error' as ILogLevel,
}
